import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, message, Modal, Space, Spin, Switch, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, EditOutlined, PlusOutlined, QrcodeOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../services/api';

const { Title, Text } = Typography;

const HairDyeSpecManagement = () => {
    const { dyeId } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [dye, setDye] = useState(null);
    const [specs, setSpecs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editorVisible, setEditorVisible] = useState(false);
    const [editingSpec, setEditingSpec] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [dyeResponse, specsResponse] = await Promise.all([
                apiService.hairDye.getById(dyeId),
                apiService.hairDye.getSpecs(dyeId, true)
            ]);
            if (dyeResponse.code === 200) {
                setDye(dyeResponse.data);
            }
            if (specsResponse.code === 200) {
                setSpecs(Array.isArray(specsResponse.data) ? specsResponse.data : []);
            }
        } catch (error) {
            message.error('获取染膏规格失败');
        } finally {
            setLoading(false);
        }
    }, [dyeId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const openCreate = () => {
        setEditingSpec(null);
        form.resetFields();
        form.setFieldsValue({ unit: 'g', isDefault: specs.length === 0, isValid: true });
        setEditorVisible(true);
    };

    const openEdit = (record) => {
        setEditingSpec(record);
        form.setFieldsValue({ ...record, unit: 'g' });
        setEditorVisible(true);
    };

    const saveSpec = async (values) => {
        try {
            const payload = { ...values, unit: 'g' };
            const response = editingSpec
                ? await apiService.hairDye.updateSpec(editingSpec.id, payload)
                : await apiService.hairDye.createSpec(dyeId, payload);
            if (response.code !== 200) {
                message.error(response.message || '规格保存失败');
                return;
            }
            message.success(editingSpec ? '规格更新成功' : '规格创建成功');
            setEditorVisible(false);
            await loadData();
        } catch (error) {
            message.error(error?.message || '规格保存失败');
        }
    };

    const columns = [
        { title: '规格ID', dataIndex: 'id', key: 'id', width: 90 },
        { title: '规格名称', dataIndex: 'specName', key: 'specName' },
        { title: '净含量', key: 'capacity', render: (_, record) => `${record.capacity}${record.unit || 'g'}` },
        {
            title: '默认规格',
            dataIndex: 'isDefault',
            key: 'isDefault',
            render: value => value ? <Tag color="blue">默认</Tag> : '-'
        },
        {
            title: '状态',
            dataIndex: 'isValid',
            key: 'isValid',
            render: value => <Tag color={value ? 'green' : 'red'}>{value ? '启用' : '停用'}</Tag>
        },
        {
            title: '操作',
            key: 'action',
            render: (_, record) => (
                <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(record)}>
                    编辑
                </Button>
            )
        }
    ];

    return (
        <Card>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 20 }}>
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/hair-dye')}>返回染膏管理</Button>
                    <Title level={4} style={{ margin: 0 }}>规格管理</Title>
                </Space>
                <Button icon={<QrcodeOutlined />} onClick={() => navigate(`/hair-dye/stock/${dyeId}`)}>
                    二维码库存
                </Button>
            </Space>

            {loading && !dye ? <Spin /> : (
                <Text type="secondary">
                    当前染膏：{dye?.dyeName || '-'} / {dye?.brand || '-'} / 色号 {dye?.colorNumber || '-'}
                </Text>
            )}

            <div style={{ margin: '20px 0 16px' }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增规格</Button>
            </div>

            <Table rowKey="id" columns={columns} dataSource={specs} loading={loading} pagination={false} />

            <Modal
                title={editingSpec ? '编辑规格' : '新增规格'}
                visible={editorVisible}
                onCancel={() => setEditorVisible(false)}
                onOk={() => form.submit()}
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={saveSpec}>
                    <Form.Item name="specName" label="规格名称" rules={[{ required: true, message: '请输入规格名称' }]}>
                        <Input placeholder="例如：500g" />
                    </Form.Item>
                    <Form.Item name="capacity" label="净含量(g)" rules={[{ required: true, message: '请输入净含量' }]}>
                        <InputNumber min={0.01} precision={2} addonAfter="g" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="isDefault" label="默认规格" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                    <Form.Item name="isValid" label="启用" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default HairDyeSpecManagement;
